import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, Loader2, ArrowRight } from "lucide-react";
import { useCart } from "../context/CartContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Reveal } from "../components/Reveal";

const FREE_SHIP = 65;
const LOCAL_NOTE = "Local delivery available within the Columbus metro area. Delivery within 2 to 3 business days of order confirmation.";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal, lineKey } = useCart();
  const [loading, setLoading] = useState(false);
  const [promo, setPromo] = useState("");
  const [localApplied, setLocalApplied] = useState(false);

  const applyPromo = () => {
    if (promo.trim().toUpperCase() === "LOCALDELIVERY") {
      setLocalApplied(true);
      toast.success("Local delivery unlocked — you'll see it as a shipping option at checkout.");
    } else {
      toast.error("Code not recognized");
    }
  };

  const checkout = async () => {
    if (!items.length) return;
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        origin_url: window.location.origin,
        promo_code: localApplied ? "LOCALDELIVERY" : undefined,
        items: items.map((i) => ({
          product_id: i.product_id,
          name: i.name,
          size: i.size,
          variant: i.variant || null,
          quantity: i.quantity,
        })),
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Checkout failed");
      setLoading(false);
    }
  };

  const remaining = Math.max(0, FREE_SHIP - subtotal);

  return (
    <div className="pt-28 min-h-screen" data-testid="cart-page">
      <section className="mx-auto max-w-[1200px] px-5 sm:px-8 py-12">
        <Reveal>
          <h1 className="font-serif text-5xl sm:text-6xl text-moss">Your Cart</h1>
        </Reveal>

        {items.length === 0 ? (
          <div className="mt-16 rounded-sm bg-cream-2 p-16 text-center">
            <p className="font-serif text-3xl text-moss">Nothing here yet.</p>
            <Link to="/shop" className="btn-gold mt-6 inline-flex items-center gap-2 rounded-sm px-7 py-3.5 text-sm font-semibold uppercase tracking-wider" data-testid="cart-empty-shop">
              Shop the butters <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        ) : (
          <div className="mt-12 grid gap-12 lg:grid-cols-[1.6fr_1fr]">
            <div className="divide-y divide-[#E4DDD0] border-y border-[#E4DDD0]">
              {items.map((i) => {
                const key = lineKey(i);
                return (
                  <div key={key} className="flex gap-5 py-6" data-testid={`cart-page-item-${i.product_id}`}>
                    <img src={i.image} alt={i.name} className="h-32 w-28 rounded-sm object-cover bg-cream-3" />
                    <div className="flex flex-1 flex-col">
                      <div className="flex justify-between gap-4">
                        <h3 className="font-serif text-2xl text-moss">{i.name}</h3>
                        <span className="font-serif text-xl text-moss">${(i.price * i.quantity).toFixed(2)}</span>
                      </div>
                      <p className="mt-1 font-mono text-xs text-muted">{i.size}{i.variant ? ` · ${i.variant}` : ""}</p>
                      <div className="mt-auto flex items-center gap-4 pt-4">
                        <div className="flex items-center border border-[#E4DDD0] rounded-sm">
                          <button onClick={() => updateQty(key, i.quantity - 1)} className="px-3 py-2 hover:text-gold" data-testid={`cart-page-dec-${i.product_id}`}><Minus className="h-3 w-3" /></button>
                          <span className="px-3 text-sm" data-testid={`cart-page-qty-${i.product_id}`}>{i.quantity}</span>
                          <button onClick={() => updateQty(key, i.quantity + 1)} className="px-3 py-2 hover:text-gold" data-testid={`cart-page-inc-${i.product_id}`}><Plus className="h-3 w-3" /></button>
                        </div>
                        <button onClick={() => removeItem(key)} className="flex items-center gap-1 text-xs text-muted hover:text-terracotta" data-testid={`cart-page-remove-${i.product_id}`}>
                          <Trash2 className="h-4 w-4" /> Remove
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="h-fit rounded-sm bg-cream-2 p-8 lg:sticky lg:top-24">
              <h2 className="font-serif text-3xl text-moss mb-6">Summary</h2>
              <div className="flex justify-between py-2 text-sm">
                <span className="text-muted">Subtotal</span>
                <span className="text-moss" data-testid="cart-page-subtotal">${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between py-2 text-sm border-b border-[#E4DDD0] pb-4">
                <span className="text-muted">Shipping</span>
                <span className="text-moss">{remaining > 0 ? "$6.99 standard · set at checkout" : "Free ✦"}</span>
              </div>
              {remaining > 0 && (
                <p className="mt-3 text-xs text-muted font-mono">Add ${remaining.toFixed(2)} for free shipping</p>
              )}

              {localApplied ? (
                <p className="mt-4 rounded-sm border border-gold/40 bg-cream p-3 text-xs text-moss leading-relaxed" data-testid="cart-local-note">
                  {LOCAL_NOTE}
                </p>
              ) : (
                <div className="mt-4 flex items-center gap-2">
                  <input
                    value={promo}
                    onChange={(e) => setPromo(e.target.value)}
                    placeholder="Delivery code"
                    className="w-full bg-transparent border-b border-moss/30 py-2 text-sm placeholder:text-muted focus:outline-none focus:border-gold"
                    data-testid="cart-promo-input"
                  />
                  <button onClick={applyPromo} className="shrink-0 font-mono text-xs uppercase tracking-wider text-gold hover:text-moss transition-colors" data-testid="cart-promo-apply">
                    Apply
                  </button>
                </div>
              )}

              <div className="flex justify-between py-4 mt-2">
                <span className="font-serif text-xl text-moss">Total</span>
                <span className="font-serif text-2xl text-moss" data-testid="cart-page-total">${subtotal.toFixed(2)}</span>
              </div>
              <button
                onClick={checkout}
                disabled={loading}
                className="btn-gold w-full rounded-sm py-4 text-sm font-semibold uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="cart-page-checkout"
              >
                {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Redirecting…</> : "Proceed to Checkout"}
              </button>
              <p className="mt-3 text-center text-[11px] font-mono text-muted">Card · Apple Pay · Google Pay via Stripe</p>
              <p className="mt-2 text-center text-[11px] text-muted" data-testid="cart-processing-note">Please allow 3–5 business days for order processing before shipment.</p>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
